// Generic text-input prompt dialog replacing window.prompt — in-app, theme-consistent,
// keyboard-accessible (Escape to cancel, Enter to confirm).
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { Dialog } from "@grackle/ui";
import { Button, Field } from "./ui";

interface Props {
  open: boolean;
  title: string;
  message: string;
  placeholder?: string;
  onClose: () => void;
  onConfirm: (value: string) => void;
}

export default function ConfirmPrompt({
  open,
  title,
  message,
  placeholder,
  onClose,
  onConfirm,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");

  // `onClose` is routed through a ref so this effect depends on `open` alone — with the
  // callback in the deps, a parent re-render (callers pass inline closures) would re-run
  // the effect and wipe whatever the user has typed.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    setValue("");
    inputRef.current?.focus();

    function onKeydown(e: KeyboardEvent) {
      if (e.key === "Escape") onCloseRef.current();
    }
    document.addEventListener("keydown", onKeydown);
    return () => document.removeEventListener("keydown", onKeydown);
  }, [open]);

  if (!open) return null;

  const submit = () => {
    const trimmed = value.trim();
    if (trimmed) onConfirm(trimmed);
  };

  return (
    <Dialog
      role="alertdialog"
      title={title}
      width={420}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!value.trim()}>
            Confirm
          </Button>
        </>
      }
    >
      <div className="dialog-message">{message}</div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field
          ref={inputRef}
          label="Value"
          placeholder={placeholder}
          value={value}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
          autoFocus
        />
      </form>
    </Dialog>
  );
}
